import { useRef, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Button from "../button";

const getScrollableAncestors = (el) => {
    const ancestors = [];
    let node = el?.parentElement;
    while (node && node !== document.body) {
        const { overflow, overflowX, overflowY } = window.getComputedStyle(node);
        const ox = `${overflow} ${overflowX} ${overflowY}`;
        if (/(auto|scroll|overlay)/.test(ox)) {
            ancestors.push(node);
        }
        node = node.parentElement;
    }
    return ancestors;
};

const ExternalTooltip = ({ title, description, confirm, setOnClickConfirm, children }) => {
    const triggerRef = useRef(null);
    const [coords, setCoords] = useState(null);

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setCoords({
            left: rect.left + rect.width / 2,
            top: rect.bottom,
        });
    }, []);

    useLayoutEffect(() => {
        if (confirm) {
            setCoords(null);
            return undefined;
        }

        const el = triggerRef.current;
        if (!el) return undefined;

        updatePosition();

        const scrollables = getScrollableAncestors(el);
        scrollables.forEach((node) => node.addEventListener("scroll", updatePosition, true));
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);

        return () => {
            scrollables.forEach((node) => node.removeEventListener("scroll", updatePosition, true));
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [confirm, updatePosition, children]);

    const handleConfirm = () => {
        setOnClickConfirm(true);
    };

    const tooltip =
        !confirm &&
        coords &&
        typeof document !== "undefined" &&
        createPortal(
            <div
                className="pointer-events-auto z-[100] w-[319px] fixed transition duration-150 ease-in-out text-white"
                style={{
                    left: coords.left,
                    top: coords.top + 8,
                    transform: "translateX(-50%)",
                }}
                role="dialog"
                aria-label={title}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="h-0 w-0 border-x-8 border-x-transparent border-b-[12px] border-b-blue-500 mx-auto" />
                <div className={"bg-blue-500 rounded shadow-lg p-4 flex flex-col gap-3"}>
                    <div className="text-lg">{title}</div>
                    <div className="text-sm">{description}</div>
                    <div className="flex justify-end">
                        <Button color={"BLUE"} size={"SM"} onClick={() => handleConfirm()}>
                            D'accord
                        </Button>
                    </div>
                </div>
            </div>,
            document.body
        );

    return (
        <>
            <span ref={triggerRef} className="relative inline-flex">
                {children}
            </span>
            {tooltip}
        </>
    );
};

export default ExternalTooltip;
